import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
  AfterViewInit,
  AfterViewChecked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { myCustomTheme } from '../../../constants/grid-themes';
import { UserHubNotification } from '@models/notification.model';
import { NotificationsUserActions } from '@store/actions/notifications/notifications.actions';
import {
  selectUserNotifications,
  selectNotificationsLoading,
} from '@store/selectors/notifications/notifications.selectors';
import { NotificationUserViewModalComponent } from '../../modals/notification-user-view-modal/notification-user-view-modal.component';
import { NotificationsUserActionsRendererComponent } from './notifications-user-actions-renderer.component';

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, AgGridAngular
  ],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.css',
})
export class NotificationsComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly destroy$ = new Subject<void>();

  theme = myCustomTheme;
  private unreadGridApi: any;
  private archivedGridApi: any;

  unreadNotifications: UserHubNotification[] = [];
  archivedNotifications: UserHubNotification[] = [];
  isLoading = true;

  private readonly dateFormatter = (p: any) => {
    if (!p.value) return '';
    const [year, month, day] = (p.value as string).split('T')[0].split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  private readonly notificationCellRenderer = (params: any) => {
    const title = params.value ?? '';
    if (params.data?.priority === 'High') {
      return `<span class="high-priority-indicator" title="High Priority">&#33;</span>${title}`;
    }
    return title;
  };

  unreadColDefs: ColDef[] = [
    {
      field: 'title',
      headerName: 'Notification',
      flex: 2,
      cellRenderer: this.notificationCellRenderer,
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      field: 'createdDate',
      headerName: 'Date',
      flex: 1,
      valueFormatter: this.dateFormatter,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      flex: 1,
    },
    {
      headerName: 'Actions',
      colId: 'unread-actions',
      minWidth: 110,
      width: 110,
      pinned: 'right',
      filter: false,
      sortable: false,
      resizable: false,
      cellRenderer: NotificationsUserActionsRendererComponent,
    },
  ];

  archivedColDefs: ColDef[] = [
    {
      field: 'title',
      headerName: 'Notification',
      flex: 2,
      cellRenderer: this.notificationCellRenderer,
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      field: 'createdDate',
      headerName: 'Date',
      flex: 1,
      valueFormatter: this.dateFormatter,
    },
    {
      field: 'readDate',
      headerName: 'Read',
      flex: 1,
      valueFormatter: this.dateFormatter,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      flex: 1,
    },
    {
      headerName: 'Actions',
      colId: 'archived-actions',
      minWidth: 110,
      width: 110,
      pinned: 'right',
      filter: false,
      sortable: false,
      resizable: false,
      cellRenderer: NotificationsUserActionsRendererComponent,
    },
  ];

  defaultColDef: ColDef = {
    filter: true,
    resizable: true,
    sortable: true,
    editable: false,
    minWidth: 100,
  };

  unreadGridOptions: GridOptions = {
    rowSelection: 'single',
    pagination: true,
    paginationPageSize: 20,
    suppressClickEdit: true,
    loading: true,
    rowData: [],
    onGridReady: (p: GridReadyEvent) => { this.unreadGridApi = p.api; p.api.setGridOption('loading', this.isLoading); },
  };

  archivedGridOptions: GridOptions = {
    rowSelection: 'single',
    pagination: true,
    paginationPageSize: 20,
    suppressClickEdit: true,
    loading: true,
    rowData: [],
    onGridReady: (p: GridReadyEvent) => { this.archivedGridApi = p.api; p.api.setGridOption('loading', this.isLoading); },
  };

  ngOnInit(): void {
    this.store.select(selectNotificationsLoading).pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.isLoading = loading;
      this.unreadGridApi?.setGridOption('loading', loading);
      this.archivedGridApi?.setGridOption('loading', loading);
    });

    this.store.select(selectUserNotifications).pipe(takeUntil(this.destroy$)).subscribe((notifications) => {
      this.unreadNotifications = notifications.filter((n) => !n.isRead);
      this.archivedNotifications = notifications.filter((n) => n.isRead);
      if (!this.isLoading) {
        this.unreadGridApi?.setGridOption('rowData', this.unreadNotifications);
        this.archivedGridApi?.setGridOption('rowData', this.archivedNotifications);
      }
    });

    this.store.dispatch(NotificationsUserActions.loadUserNotifications());
  }

  ngAfterViewInit(): void { this.cdr.detectChanges(); }
  ngAfterViewChecked(): void { this.cdr.detectChanges(); }

  onUnreadCellClicked(params: any): void {
    const action = params.event?.target?.dataset?.action;
    if (!action) return;
    const data = params.data as UserHubNotification;

    if (action === 'view') {
      this.openViewModal(data, false);
    } else if (action === 'markRead') {
      this.store.dispatch(NotificationsUserActions.markNotificationRead({ id: data.id }));
    }
  }

  onArchivedCellClicked(params: any): void {
    const action = params.event?.target?.dataset?.action;
    if (!action) return;
    const data = params.data as UserHubNotification;

    if (action === 'view') {
      this.openViewModal(data, true);
    } else if (action === 'markUnread') {
      this.store.dispatch(NotificationsUserActions.markNotificationUnread({ id: data.id }));
    }
  }

  private openViewModal(notification: UserHubNotification, isArchived: boolean): void {
    this.dialog.open(NotificationUserViewModalComponent, {
      width: '520px',
      disableClose: true,
      data: { notification, isArchived },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
